<template>
	<div class="user-profile">
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<v-card class="user-profile__card pb-1" flat>
				<h2 class="user-profile__title">
					User Profile
				</h2>
				<v-card-text class="mt-5">
					<div v-if="!isAttendeeEmpty">
						<div class="user-profile__header mt-1" v-bind:style="{paddingTop: userFlag ? '32px' : '16px'}">
							<ProfileBadge v-if="userFlag" :badge="userFlag" />
							<div class="user-profile__header-content">
								<Avatar 
									:avatar="avatarPath" size="120px" 
									border-radius="square"
									class="user-profile__avatar mr-4" 
								/>
								<div class="user-profile__details">
									<h1 class="user-profile__name">
										{{ userName }}
									</h1>
									<div class="d-flex flex-column">
										<span v-if="companyName" class="user-profile__company mb-1">
											{{ companyName }}
										</span>
										<span v-if="heldPosition" class="user-profile__position">
											{{ heldPosition }}
										</span>
										<span v-if="location" class="user-profile__location">
											<Icon 
												type="pin" 
												color="var(--location-color)" 
												:size="14" 
												:pr="4" 
											/>
											<span>
												{{ location }}
											</span>
										</span>
									</div>
								</div>
							</div>
							<v-btn class="user-profile__note-btn elevation-0" @click="switchNoteFormModal">
								{{ attendee.follow_note ? 'Edit Note' : 'Add Note' }}
							</v-btn>
						</div>

						<div v-if="attendee.follow_note" class="user-profile__note">
							<h3 class="user-profile__section-title">
								Note
							</h3>
							<div class="user-profile__text">
								{{ attendee.follow_note }}
							</div>
						</div>

						<div 
							v-for="(section, index) in allowSections" 
							:key="index" 
							class="user-profile__section"
						>
							<h3 class="user-profile__section-title">
								{{ section[0].section }}
							</h3>
							<div 
								v-for="field in section" 
								:key="field.field" 
								class="user-profile__field"
							>
								<div v-if="field.type === 'email'" class="user-profile__text d-flex flex-nowrap align-center">
									<Icon type="email" color="var(--accent-color)" :pr="12" :size="22" />
									<a :href="'mailto:' + field.value" style="text-decoration: none; color: var(--accent-color);">
										{{ field.value }}
									</a>
								</div>
								<a 
									v-else-if="networkTypes.some(type => type === field.type)"
									:href="field.value"
									target="_blank"
									class="user-profile__network"
								>
									<h3 class="user-profile__network-title">
										{{ field.title }}
									</h3>
									<div class="d-flex align-center">
										<Icon 
											:size="28"
											:type="networkFieldType(field.type)"
										/>
										<span class="user-profile__network-link-text ml-2">
											{{ field.value }}
										</span>
									</div>
								</a>
								<div v-else-if="field.type === 'phone'" class="user-profile__phone py-3 d-flex">
									<Icon 
										type="phone" 
										color="var(--accent-color)" 
										:pr="12" 
										:size="22" 
									/>
									<a 
										@click="copyPhone(field.value)" 
										href="#" 
										class="user-profile__phone-link"
									>
										{{ field.value }}
									</a>
								</div>
								<div v-else-if="field.type === 'html'" v-html="field.value" />
								<div 
									v-else-if="field.section !== 'Network'" 
									class="user-profile__field-content mb-2"
								>
									<span class="user-profile__field-label">
										{{ field.title }}:
									</span>
									<span class="user-profile__field-value">
										{{ field.value }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<v-skeleton-loader 
						v-if="isAttendeeEmpty" 
						style="width: 100%;" 
						v-bind="attrs" 
						type="card"
					/>
				</v-card-text>
				<div v-if="isShowNoteFormModal">
					<NoteFormModal 
						v-on:closeModal="switchNoteFormModal" 
						:show="isShowNoteFormModal" 
						:id="userId"
						:username="userName" 
						:edit="!!attendee.follow_note" 
						:note="attendee.follow_note || null"
						userPage 
					/>
				</div>
			</v-card>
		</div>
	</div>
</template>


<script>
import { mapActions, mapGetters } from 'vuex'
import searchIcon from '@/assets/images/search.svg'
import Avatar from '@/components/Avatar'
import NoteFormModal from '../../components/Modals/NoteFormModal.vue'
import Block from '@/mixins/Block.js'
import Dwh from '@/mixins/Dwh'
import PageBlocked from '@/components/PageBlocked'
import ProfileBadge from '@/components/ProfileBadge.vue'
import _ from 'lodash'

export default {
	name: 'ProfilePage',
	mixins: [Block, Dwh],
	components: {
		Avatar,
		NoteFormModal,
		PageBlocked,
		ProfileBadge
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		...mapGetters('event', ['attendee']),
		...mapGetters('settings', ['backUser']),

		isAttendeeEmpty() {
			const {
				user_id: userId,
			} = this.attendee || {}

			return userId !== +this.$route.params.id
		},
		allowSections() {
			return this.attendee.public.filter(field => field.value).reduce((acc, current) => {
				if (acc.length === 0 || acc[acc.length - 1][0].section !== current.section) {
					acc.push([current]);
				} else {
					acc[acc.length - 1].push(current);
				}
				return acc;
			}, []);
		},
		isVerticalTabs() {
			return this.$vuetify.breakpoint.mdAndUp
		},
		userFlag() {
			const { userFlag: flag } = this.attendee || {};

			return flag
		},
		userId() {
			const { user_id: userId } = this.attendee || {}
			return userId
		},

		// short profile
		location() {
			const { location_profile_row: location = {} } = this.attendee || {}
			return location
		},
		userName() {
			const { visible_username: name } = this.attendee || {}
			return name
		},
		avatarPath() {
			const { user_logo: userLogo } = this.attendee || {}
			return userLogo
		},
		heldPosition() {
			const { third_profile_row: heldPosition } = this.attendee || {}
			return heldPosition
		},
		companyName() {
			const { second_profile_row: company } = this.attendee || {}
			return company
		},
	},
	methods: {
		...mapActions('event', ['getAttendee']),
		...mapActions('app', ['showAlert', 'hideAlert']),

		onTabSwitch() {
			this.successMessage = false
			this.errorMessage = false
		},
		normalizeSettings(item) {
			const { setting, value } = item

			let settings = {}
			settings[setting.name] = value !== '0'

			return settings
		},
		dateCreated(originalDate) {
			return new Date(originalDate * 1000).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})
		},
		getTypeFieldsFromUrl(url) {
			const parts = url.split('/')
			return parts[parts.length - 1]
		},
		switchNoteFormModal() {
			this.isShowNoteFormModal = !this.isShowNoteFormModal
		},
		formatUrl(url) {
			if (!url) {
				return '';
			}

			if (!url.startsWith('http://') && !url.startsWith('https://')) {
				if (url.startsWith('www.')) {
					url = 'https://' + url;
				} else {
					url = 'https://www.' + url;
				}
			}
			return url;
		},
		copyPhone(value) {
			if (!this.isShowAlert) {
				const phone = value.replace(/\D/g, '');

				this.$copyText(phone).then(() => {
					const closeTimeout = setTimeout(() => {
						this.hideAlert('')
					}, 3000)

					this.showAlert({ message: 'Phone number copied successfully.', closeTimeout })
				})
			}
		},
		fieldClass(allowFields, index) {
			if (index === 0 || allowFields[index].section !== allowFields[index - 1].section) {
				return 'mt-8'
			} else if (!allowFields[index + 1] || allowFields[index + 1].section !== allowFields[index].section) {
				return 'mb-8'
			} else {
				return ''
			}
		},
		networkFieldType(type) {
			if (type === 'url') {
				return 'link'
			} else {
				return type
			}
		}
	},
	watch: {
		attendee(newValue, oldValue) {
			if ((newValue && !oldValue) || !_.isEqual(newValue, oldValue)) {
				this.sendTrigger({
					category: 'User',
					action: 'view_user_profile',
					module: 'App Users',
					data: newValue.user_id,
					title: newValue.visible_username
				})
			}
		}
	},
	data() {
		return {
			searchIcon,
			successMessage: false,
			errorMessage: false,
			isShowNoteFormModal: false,
			attrs: {
				class: 'mb-6 mt-6',
				elevation: 0,
			},
			socialFieldNames: [
				'user_linkedin',
				'user_facebook',
				'user_twitter',
				'user_custom_url',
			],
			phoneFieldNames: [
				'user_work_phone',
				'user_cell_phone',
			],
			networkTypes: ['url', 'linkedin', 'facebook', 'twitter', 'instagram'],
			pageBlockedName: 'users'
		}
	},
	created() {
		if (this.$route.params.id && (!this.isBlocked || this.isBlocked === null)) {
			this.getAttendee(+this.$route.params.id)
		}
	},
}
</script>

<style lang='scss'>
.user-profile {
	&__title {
		font-family: Montserrat, sans-serif;
		font-size: 24px;
		font-weight: 700;
		line-height: 29.26px;
		text-align: left;
	}

	&__card {
		background-color: transparent !important;
		max-width: 600px !important;
	}

	&__header,
	&__note,
	&__section {
		position: relative;
		max-width: 600px !important;
		border-radius: 10px !important;
		margin-bottom: 8px;
		padding: 16px;
		background-color: #fff;
		font-family: Montserrat, sans-serif;

		& > div:not(:last-child) > a.user-profile__network {
			margin-bottom: 12px;
		}
	}

	&__section-title {
		max-width: 600px !important;
		font-size: 16px;
		font-weight: 700;
		line-height: 19.5px;
		color: #4A4A4A;
		margin-bottom: 12px;

	}

	&__field-label {
		font-size: 14px;
		font-weight: 700;
		line-height: 17.07px;
		color: var(--accent-color);
		margin-right: 12px;
	}

	&__text,
	&__field-value {
		font-size: 14px;
		line-height: 17.07px;
		color: #697583;
	}

	&__network {
		display: block;
		text-decoration: none;

		&-link {
			&-text {
				font-size: 14px;
				line-height: 17.07px;
				color: var(--accent-color);
				width: 100%;
				overflow: hidden;
				text-overflow: ellipsis;
				display: -webkit-box;
				-webkit-line-clamp: 1;
				-webkit-box-orient: vertical;
			}
		}

		&-title {
			font-size: 14px;
			font-weight: 600;
			line-height: 22px;
			color: #4A4A4A;
			margin-bottom: 4px;
		}
	}

	&__header-content {
		display: flex;
		flex-wrap: nowrap;
		margin-bottom: 20px;

		@media (max-width: 480px) {
			flex-direction: column;
			align-items: center;
		}
	}

	&__details {
		font-family: Montserrat, sans-serif;
		width: 100%;
	}

	&__name {
		font-size: 24px;
		line-height: 29.26px;
		font-weight: 700;
		color: var(--accent-color);
		margin-bottom: 12px;

		@media (max-width: 480px) {
			margin-top: 12px;
			text-align: center;
		}
	}

	&__company,
	&__position {
		font-size: 16px;
		line-height: 19.5px;
		color: #4A4A4A;
		width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;

		@media (max-width: 480px) {
			text-align: center;
		}
	}

	&__location {
		font-size: 13px;
		line-height: 19.37px;
		font-weight: 500;
		color: var(--location-color);
		display: flex;
		align-items: center;
		flex-wrap: nowrap;
		margin-top: 12px;

		@media (max-width: 480px) {
			justify-content: center;
		}
	}

	&__note-btn {
		height: 42px !important;
		padding: 0 38px !important;
		font-size: 18px;
		font-weight: 700;
		line-height: 21.94px;
		text-transform: capitalize;
		font-family: Montserrat, sans-serif;
		color: var(--button-secondary-background-color) !important;
		border: 2px solid var(--button-secondary-background-color);
		background-color: var(--button-secondary-color) !important;
		border-radius: 8px;
	}

	&__phone-link {
		text-decoration: none;
		color: var(--ceil-detail-text-color);
	}
}
</style>
